import { Box, Button, Typography, useTheme } from '@mui/material';
import { ReactElement } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faTriangleExclamation,
  faSpinner,
  faInbox,
} from '@fortawesome/free-solid-svg-icons';

export type EmptyStateVariant = 'no-result' | 'no-data' | 'loading' | 'error' | 'empty';
export type EmptyStateSize = 'sm' | 'md' | 'lg';

export interface EmptyStateProps {
  variant: EmptyStateVariant;
  message?: string;
  retry?: () => void;
  size?: EmptyStateSize;
}

const VARIANT_DEFAULTS: Record<
  EmptyStateVariant,
  { icon: typeof faCircleInfo; defaultMessage: string }
> = {
  'no-result': { icon: faCircleInfo, defaultMessage: 'No results.' },
  'no-data': { icon: faInbox, defaultMessage: 'No data.' },
  loading: { icon: faSpinner, defaultMessage: 'Loading…' },
  error: { icon: faTriangleExclamation, defaultMessage: 'Something went wrong.' },
  empty: { icon: faInbox, defaultMessage: 'Empty.' },
};

const SIZE_TOKENS: Record<EmptyStateSize, { icon: string; gap: number; fontSize: string }> = {
  sm: { icon: '16px', gap: 0.5, fontSize: '0.75rem' },
  md: { icon: '24px', gap: 1, fontSize: '0.875rem' },
  lg: { icon: '32px', gap: 1.5, fontSize: '1rem' },
};

const EmptyState = ({ variant, message, retry, size = 'md' }: EmptyStateProps): ReactElement => {
  const theme = useTheme();
  const { icon, defaultMessage } = VARIANT_DEFAULTS[variant];
  const tokens = SIZE_TOKENS[size];
  const colour = variant === 'error' ? theme.palette.error.main : theme.palette.text.secondary;

  return (
    <Box
      role="status"
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: tokens.gap,
        padding: 2,
        textAlign: 'center',
      }}
    >
      <FontAwesomeIcon
        icon={icon}
        style={{ fontSize: tokens.icon, color: colour }}
        spin={variant === 'loading'}
      />
      <Typography sx={{ fontSize: tokens.fontSize, color: colour }}>
        {message ?? defaultMessage}
      </Typography>
      {retry && (
        <Button size="small" variant="text" onClick={retry}>
          Retry
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
